import { useState, useMemo, useRef, useCallback } from "react";
import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { TruncatedText } from "@/components/ui/truncated-text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TrendingUp,
  Gem,
  Trophy,
  Zap,
  Eye,
  Sparkles,
  Sparkle,
  Star,
  Download,
  Loader2,
  MoreVertical,
  Share2,
  CalendarDays,
} from "lucide-react";
import { BulkActionsBar } from "@/components/shared/BulkActionsBar";
import { toast as toastHook } from "@/hooks/use-toast";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Keyword, Product, SeoInsightKeywordItem, isSeoInsightPending } from "@/types";
import type { SeoKeyword, PublicSeoInsightsPayload } from "@/components/share-views/PublicSeoInsightsView";
import { formatKeywordPendingNumber } from "@/utils/keywordPendingDisplay";
import { productApi } from "@/resources/product";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { seoInsightApi } from "@/resources/seo-insight";
import { KeywordMasterListAction } from "@/constants";
import { useAppData } from "@/contexts/AppDataContext";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { handleApiError, dedupeBySearch } from "@/utils";
import { ShareLinkModal } from "@/components/shared/ShareLinkModal";
import { ValidationPanel } from "@/components/ui/ValidationPanel";


function insightKeywordToKeyword(item: SeoInsightKeywordItem, index: number): Keyword {
  return {
    id: index,
    search: item.keyword,
    product_id: 0,
    rank: null,
    search_volume: item.search_volume,
    search_volume_trend: null,
    kps: item.kps,
    landscape_key: null,
    created_at: null,
    updated_at: null,
    landscape_id: null,
    ppc: null,
    ppc_min_bid: null,
    ppc_max_bid: null,
    keyword_sales: null,
    asin_density: null,
    competitors: null,
    hero_rank: null,
    ranks: null,
    serp_match_pct: null,
    serp_match_organic_pct: null,
    serp_match_sponsored_pct: null,
    serp_match_scored: null,
    serp_match_found: null,
    serp_match_week: null,
    hero_relevance: null,
    includes_hero_brand: false,
    includes_competitor_brand: false,
    no_overlap_with_hero_content: false,
    kps_raw: item.kps_raw,
    kps_search_score: null,
    kps_search_trend_score: null,
    kps_hero_rank_score: null,
    kps_hero_relevance_score: null,
    kps_ppc_score: null,
    kps_asin_density_score: null,
    kps_keyword_sales_score: null,
    kps_max_hero_relevance_score: null,
    kps_max_search_score: null,
    kps_max_asin_density_score: null,
    kps_max_hero_rank_score: null,
    kps_max_search_trend_score: null,
    kps_max_keyword_sales_score: null,
    kps_hero_relevance_factor: null,
    hero_relevance_adjusted: null,
    rank_if_converted: null,
    individual_contribution: null,
    conversion: null,
    ranked_from_1_to_15: null,
    ranked_from_16_to_40: null,
    ranked_from_41_to_150: null,
    ranked_from_151: null,
    ranked_from_1: null,
    is_master_list: false,
    landscape: null,
  };
}

function seoKeywordToKeyword(sk: SeoKeyword): Keyword {
  return {
    id: sk.id,
    search: sk.search,
    product_id: 0,
    rank: null,
    search_volume: sk.search_volume,
    search_volume_trend: null,
    kps: null,
    landscape_key: null,
    created_at: null,
    updated_at: null,
    landscape_id: null,
    ppc: null,
    ppc_min_bid: null,
    ppc_max_bid: null,
    keyword_sales: null,
    asin_density: null,
    competitors: null,
    hero_rank: null,
    ranks: null,
    serp_match_pct: null,
    serp_match_organic_pct: null,
    serp_match_sponsored_pct: null,
    serp_match_scored: null,
    serp_match_found: null,
    serp_match_week: null,
    hero_relevance: sk.hero_relevance,
    includes_hero_brand: false,
    includes_competitor_brand: false,
    no_overlap_with_hero_content: false,
    kps_raw: sk.kps_raw,
    kps_search_score: null,
    kps_search_trend_score: null,
    kps_hero_rank_score: null,
    kps_hero_relevance_score: null,
    kps_ppc_score: null,
    kps_asin_density_score: null,
    kps_keyword_sales_score: null,
    kps_max_hero_relevance_score: null,
    kps_max_search_score: null,
    kps_max_asin_density_score: null,
    kps_max_hero_rank_score: null,
    kps_max_search_trend_score: null,
    kps_max_keyword_sales_score: null,
    kps_hero_relevance_factor: null,
    hero_relevance_adjusted: null,
    rank_if_converted: null,
    individual_contribution: null,
    conversion: null,
    ranked_from_1_to_15: null,
    ranked_from_16_to_40: null,
    ranked_from_41_to_150: null,
    ranked_from_151: null,
    ranked_from_1: null,
    is_master_list: false,
    landscape: null,
  };
}

interface SeoInsightsContentV2Props {
  productId?: number;
  product?: Product;
  isPublic?: boolean;
  publicData?: PublicSeoInsightsPayload;
}

export function SeoInsightsContentV2({ productId = 0, product, isPublic = false, publicData }: SeoInsightsContentV2Props) {
  const viewerReadOnly = useViewerReadOnly();
  const readOnly = !isPublic && viewerReadOnly;
  const { selectedBrandId, brands } = useAppData();
  const [selectedInsightId, setSelectedInsightId] = useState<number | null>(null);
  const [selectedKeywords, setSelectedKeywords] = useState<Set<number>>(new Set());
  const [selectedAiRecs, setSelectedAiRecs] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const { mutateAsync: bulkUpdateMasterList, isPending: isBulkMasterListUpdating } = productApi.useBulkUpdateMasterList();
  const { mutateAsync: bulkAddRankTrackerKeywords } = rankTrackerApi.useBulkAddRankTrackerKeywords();
  const { data: heroAsinData } = productApi.useGetHeroProductABTests(
    isPublic ? undefined : String(productId),
    isPublic ? undefined : selectedBrandId ?? undefined,
  );

  const isMasterListBusy = isBulkMasterListUpdating;

  const { data: heroKeywords } = productApi.useGetHeroProductKeywords(
    isPublic ? undefined : String(productId),
    isPublic ? undefined : selectedBrandId,
  );

  const { data: insightsList } = seoInsightApi.useGetSeoInsightsList(
    isPublic ? undefined : productId || undefined,
  );
  const { data: latestInsight } = seoInsightApi.useGetLatestSeoInsight(
    isPublic ? undefined : productId || undefined,
  );
  const { data: selectedInsight } = seoInsightApi.useGetSeoInsight(
    isPublic ? undefined : productId || undefined,
    selectedInsightId,
  );

  const resolvedLatestInsight = isSeoInsightPending(latestInsight) ? undefined : latestInsight;
  const activeInsight = selectedInsightId ? selectedInsight : resolvedLatestInsight;

  const insightTopPriority = useMemo(() => {
    if (!activeInsight) return [];
    return activeInsight.keywords
      .filter((k) => k.category === "top_priority")
      .sort((a, b) => a.position - b.position)
      .map((item, index) => insightKeywordToKeyword(item, index + 1));
  }, [activeInsight]);

  const insightHiddenGems = useMemo(() => {
    if (!activeInsight) return [];
    return activeInsight.keywords
      .filter((k) => k.category === "hidden_gem")
      .sort((a, b) => a.position - b.position)
      .map((item, index) => insightKeywordToKeyword(item, index + 101));
  }, [activeInsight]);

  const insightTrending = useMemo(() => {
    if (!activeInsight) return [];
    return activeInsight.keywords
      .filter((k) => k.category === "trending")
      .sort((a, b) => a.position - b.position)
      .map((item, index) => insightKeywordToKeyword(item, index + 201));
  }, [activeInsight]);

  const insightAiRecs = useMemo(() => {
    if (!activeInsight) return [];
    return activeInsight.keywords
      .filter((k) => k.category === "ai_recommendation")
      .sort((a, b) => a.position - b.position)
      .map((item) => ({
        keyword: item.keyword,
        note: item.description || "",
        kps: item.kps_raw !== null ? item.kps_raw : (item.kps !== null ? item.kps : 0),
      }));
  }, [activeInsight]);

  const topKpsKeywords = insightTopPriority;
  const hiddenGems = insightHiddenGems;
  const trendingKeywords = insightTrending;

  const publicTopPriority = useMemo(
    () => (publicData ? publicData.topPriority.map(seoKeywordToKeyword) : []),
    [publicData],
  );
  const publicHiddenGems = useMemo(
    () => (publicData ? publicData.hiddenGems.map(seoKeywordToKeyword) : []),
    [publicData],
  );
  const publicTrending = useMemo(
    () => (publicData ? publicData.trending.map(seoKeywordToKeyword) : []),
    [publicData],
  );
  const publicAiRecs = publicData ? publicData.aiRecs : [];
  const publicKeywordsAnalyzed = publicData ? publicData.summary.keywordsAnalyzed : 0;

  const filteredAiRecs = isPublic ? publicAiRecs : insightAiRecs;

  const filteredTopKps = isPublic ? publicTopPriority : insightTopPriority;
  const filteredHiddenGems = isPublic ? publicHiddenGems : insightHiddenGems;
  const filteredTrending = isPublic ? publicTrending : insightTrending;

  const resolvedKeywordsAnalyzed = isPublic
    ? publicKeywordsAnalyzed
    : activeInsight?.keywords_analyzed ?? 0;

  const reportDateDisplay = activeInsight
    ? format(new Date(activeInsight.created_at), "MMM d, yyyy")
    : new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const handleReportChange = (value: string) => {
    if (value === "latest") {
      setSelectedInsightId(null);
    } else {
      setSelectedInsightId(Number(value));
    }
  };

  const selectValue = selectedInsightId ? String(selectedInsightId) : "latest";

  const toggleKeyword = (id: number) => {
    setSelectedKeywords((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = (kws: Keyword[]) => {
    const ids = kws.map((k) => k.id);
    const allSelected = ids.every((id) => selectedKeywords.has(id));
    setSelectedKeywords((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const handleAddToMasterList = async () => {
    const selectedIds = Array.from(selectedKeywords);
    if (selectedIds.length === 0 || isMasterListBusy) return;

    const allInsightKeywords = [...insightTopPriority, ...insightHiddenGems, ...insightTrending];
    const selectedTexts = new Set(
      allInsightKeywords
        .filter((k) => selectedIds.includes(k.id))
        .map((k) => k.search.toLowerCase()),
    );

    const resolvedIds = (heroKeywords ?? [])
      .filter((k) => selectedTexts.has(k.search.toLowerCase()))
      .map((k) => k.id);

    setSelectedKeywords(new Set());

    if (resolvedIds.length === 0) {
      toastHook({
        title: "No tracked keywords found",
        description: "These keywords aren't in the keyword bank yet. Add them via Rank Tracker or generate landscapes first.",
      });
      return;
    }

    toastHook({
      title: "Adding to Master List...",
      description: `Adding ${resolvedIds.length} keyword${resolvedIds.length !== 1 ? "s" : ""} to Master List.`,
    });

    try {
      await bulkUpdateMasterList({
        productId,
        keywordIds: resolvedIds,
        action: KeywordMasterListAction.ADD,
      });
      toastHook({
        title: "Added to Master List",
        description: `${resolvedIds.length} keyword${resolvedIds.length !== 1 ? "s" : ""} added successfully.`,
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleAddToRankTracker = async () => {
    const keywordIds = Array.from(selectedKeywords);
    if (keywordIds.length === 0 || !selectedBrandId || !product) return;

    const allKeywords = [...topKpsKeywords, ...hiddenGems, ...trendingKeywords];
    // AUT-163: the same keyword can appear in multiple recommendation parts (e.g.
    // Top Priority and Trending); dedupe by search so it isn't counted or added
    // to Rank Tracker twice (matches Bulk Add to Master List).
    const keywordsToAdd = dedupeBySearch(allKeywords.filter((k) => keywordIds.includes(k.id)));
    if (keywordsToAdd.length === 0) return;

    setSelectedKeywords(new Set());

    toastHook({
      title: "Adding to Rank Tracker",
      description: `Adding ${keywordsToAdd.length} keyword${keywordsToAdd.length !== 1 ? "s" : ""}...`,
    });

    try {
      const result = await bulkAddRankTrackerKeywords({
        brandId: selectedBrandId,
        heroAsin: product.asin,
        keywords: keywordsToAdd.map((k) => k.search),
        source: "seo_insights",
      });
      if (result.inserted === 0 && result.skipped > 0) {
        toastHook({ title: "Already tracked", description: `${result.skipped} keyword${result.skipped !== 1 ? "s are" : " is"} already in the rank tracker.` });
      } else {
        toastHook({
          title: "Added to Rank Tracker",
          description: `${result.inserted} keyword${result.inserted !== 1 ? "s" : ""} added${result.skipped > 0 ? `, ${result.skipped} already tracked` : ""}.`,
        });
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const toggleAiRec = (keyword: string) => {
    setSelectedAiRecs((prev) => {
      const next = new Set(prev);
      if (next.has(keyword)) {
        next.delete(keyword);
      } else {
        next.add(keyword);
      }
      return next;
    });
  };

  const toggleAllAiRecs = () => {
    const allSelected = filteredAiRecs.every((r) => selectedAiRecs.has(r.keyword));
    setSelectedAiRecs(allSelected ? new Set() : new Set(filteredAiRecs.map((r) => r.keyword)));
  };

  const handleAiRecsAddToRankTracker = async () => {
    const aiRecsArray = Array.from(selectedAiRecs);
    if (aiRecsArray.length === 0 || !selectedBrandId || !product) return;

    setSelectedAiRecs(new Set());

    toastHook({
      title: "Adding to Rank Tracker",
      description: `Adding ${aiRecsArray.length} keyword${aiRecsArray.length !== 1 ? "s" : ""}...`,
    });

    try {
      const result = await bulkAddRankTrackerKeywords({
        brandId: selectedBrandId,
        heroAsin: product.asin,
        keywords: aiRecsArray,
        source: "signal_ai",
      });
      if (result.inserted === 0 && result.skipped > 0) {
        toastHook({ title: "Already tracked", description: `${result.skipped} keyword${result.skipped !== 1 ? "s are" : " is"} already in the rank tracker.` });
      } else {
        toastHook({
          title: "Added to Rank Tracker",
          description: `${result.inserted} keyword${result.inserted !== 1 ? "s" : ""} added${result.skipped > 0 ? `, ${result.skipped} already tracked` : ""}.`,
        });
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleAiRecsAddToMasterList = async () => {
    const aiRecsArray = Array.from(selectedAiRecs);
    if (aiRecsArray.length === 0 || isMasterListBusy) return;

    const selectedTexts = new Set(aiRecsArray.map((s) => s.toLowerCase()));
    const resolvedIds = (heroKeywords ?? [])
      .filter((k) => selectedTexts.has(k.search.toLowerCase()))
      .map((k) => k.id);

    setSelectedAiRecs(new Set());

    if (resolvedIds.length === 0) {
      toastHook({
        title: "No tracked keywords found",
        description: "These keywords aren't in the keyword bank yet. Add them via Rank Tracker or generate landscapes first.",
      });
      return;
    }

    toastHook({
      title: "Adding to Master List...",
      description: `Adding ${resolvedIds.length} keyword${resolvedIds.length !== 1 ? "s" : ""} to Master List.`,
    });

    try {
      await bulkUpdateMasterList({
        productId,
        keywordIds: resolvedIds,
        action: KeywordMasterListAction.ADD,
      });
      toastHook({
        title: "Added to Master List",
        description: `${resolvedIds.length} keyword${resolvedIds.length !== 1 ? "s" : ""} added successfully.`,
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleExport = async () => {
    if (!reportRef.current) {
      toast.error("Report not ready, try again");
      return;
    }
    setIsExporting(true);
    const element = reportRef.current;
    const originalWidth = element.style.width;
    const originalMaxWidth = element.style.maxWidth;
    element.style.width = "1200px";
    element.style.maxWidth = "none";
    void element.offsetHeight;
    try {
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        windowWidth: 1200,
        windowHeight: element.scrollHeight,
        width: element.scrollWidth,
        height: element.scrollHeight,
      });
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error("Canvas is empty");
      }

      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const pageWidthMm = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();
      const marginMm = 8;
      const contentWidthMm = pageWidthMm - marginMm * 2;
      const contentHeightMm = pageHeightMm - marginMm * 2;
      const pxPerMm = canvas.width / contentWidthMm;
      const pageHeightPx = Math.floor(contentHeightMm * pxPerMm);

      const currentBrand = brands.find((b) => b.id === selectedBrandId);
      const brandSlug = (currentBrand?.name ?? "brand")
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      const dateStr = format(new Date(), "yyyy-MM-dd");

      let srcY = 0;
      let pageNum = 0;
      while (srcY < canvas.height) {
        const sliceHeightPx = Math.min(pageHeightPx, canvas.height - srcY);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext("2d");
        if (!ctx) throw new Error("Could not get canvas context");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, -srcY);
        const sliceDataUrl = sliceCanvas.toDataURL("image/jpeg", 0.92);
        const sliceHeightMm = (sliceHeightPx / canvas.width) * contentWidthMm;
        if (pageNum > 0) pdf.addPage();
        pdf.addImage(sliceDataUrl, "JPEG", marginMm, marginMm, contentWidthMm, sliceHeightMm);
        srcY += pageHeightPx;
        pageNum += 1;
      }

      pdf.save(`seo-insights-${brandSlug}-${dateStr}.pdf`);
    } catch (error) {
      toast.error(`Export failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      element.style.width = originalWidth;
      element.style.maxWidth = originalMaxWidth;
      setIsExporting(false);
    }
  };

  const KeywordCard = ({ kw, idx, variant }: { kw: Keyword; idx?: number; variant: "priority" | "gem" | "trending" }) => {
    const isSelected = !isPublic && !readOnly && selectedKeywords.has(kw.id);
    const bgClass =
      variant === "priority"
        ? "bg-orange-500/5 border-orange-500/20"
        : variant === "gem"
          ? "bg-purple-500/5 border-purple-500/20"
          : "bg-[hsl(var(--trend-positive))]/5 border-[hsl(var(--trend-positive))]/20";

    return (
      <div
        className={cn(
          "flex items-center gap-2 p-2.5 rounded-lg border",
          !isPublic && !readOnly && "cursor-pointer transition-colors",
          bgClass,
          isSelected && "ring-1 ring-primary/50",
        )}
        onClick={() => !isPublic && !readOnly && toggleKeyword(kw.id)}
      >
        {!isPublic && !readOnly ? (
          <Checkbox checked={isSelected} onCheckedChange={() => toggleKeyword(kw.id)} onClick={(e) => e.stopPropagation()} className="flex-shrink-0 bg-white" />
        ) : null}
        {kw.is_master_list && (
          <span className="flex-shrink-0 pointer-events-none" aria-label="In Master List" title="In Master List">
            <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-600" aria-hidden />
          </span>
        )}
        {idx !== undefined && (
          <span className="text-sm font-bold text-muted-foreground w-5 flex-shrink-0">#{idx + 1}</span>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-medium text-xs">
            <TruncatedText>{kw.search}</TruncatedText>
          </p>
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <span>Vol: {(kw.search_volume ?? 0).toLocaleString()}</span>
             <span>·</span>
            {variant === "trending" && (
              <span>KPS: {formatKeywordPendingNumber(kw.kps_raw)}</span>
            )}
          </div>
        </div>
        {variant === "priority" && (
          <Badge variant="outline" className="border-orange-500/30 text-orange-600 dark:text-orange-400 text-[10px] flex-shrink-0">
            <Trophy className="h-3 w-3 mr-0.5" />Top Pick
          </Badge>
        )}
        {variant === "gem" && (
          <Badge variant="outline" className="border-purple-500/30 text-purple-600 dark:text-purple-400 text-[10px] flex-shrink-0">
            <Gem className="h-3 w-3 mr-0.5" />Gem
          </Badge>
        )}
        {variant === "trending" && (
          <Badge variant="outline" className="border-[hsl(var(--trend-positive))]/30 text-[hsl(var(--trend-positive))] text-[10px] flex-shrink-0">
            <TrendingUp className="h-3 w-3 mr-0.5" />Trending
          </Badge>
        )}
      </div>
    );
  };

  const SectionHeader = ({ keywords: sectionKeywords, icon, title, badgeLabel, tip, tipColorClass }: {
    keywords: Keyword[];
    icon: React.ReactNode;
    title: string;
    badgeLabel: string;
    tip: string;
    tipColorClass?: string;
  }) => {
    const ids = sectionKeywords.map((k) => k.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedKeywords.has(id));
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2 text-sm">
            {icon}
            {title}
            <Badge variant="secondary" className="text-[10px]">{badgeLabel}</Badge>
          </h3>
          {!isPublic && !readOnly && sectionKeywords.length > 0 && (
            <button type="button" onClick={() => toggleAll(sectionKeywords)} className="text-sm text-primary hover:text-primary/80 transition-colors">
              {allSelected ? "Deselect all" : "Select all"}
            </button>
          )}
        </div>
        <p className={cn("text-[11px] leading-relaxed", tipColorClass ?? "text-muted-foreground")}>{tip}</p>
      </div>
    );
  };

  const allAiRecsSelected = filteredAiRecs.length > 0 && filteredAiRecs.every((r) => selectedAiRecs.has(r.keyword));

  return (
    <div ref={reportRef} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Signal SEO Insights Report</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Generated on {reportDateDisplay} · {resolvedKeywordsAnalyzed} keywords analyzed
          </p>
        </div>
        {!isPublic && (
          <div data-html2canvas-ignore="true" className="flex items-center gap-2">
            <Select value={selectValue} onValueChange={handleReportChange}>
              <SelectTrigger className="w-[160px] text-xs" style={{ borderRadius: "8px" }}>
                <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <SelectValue placeholder="Select report" />
              </SelectTrigger>
              <SelectContent className="bg-popover" style={{ borderRadius: "8px" }}>
                {resolvedLatestInsight && (
                  <SelectItem value="latest" className="text-xs">
                    {format(new Date(resolvedLatestInsight.created_at), "MMM d, yyyy")} (Latest)
                  </SelectItem>
                )}
                {(insightsList ?? [])
                  .filter((item) => !resolvedLatestInsight || item.id !== resolvedLatestInsight.id)
                  .map((item) => (
                    <SelectItem key={item.id} value={String(item.id)} className="text-xs">
                      {format(new Date(item.created_at), "MMM d, yyyy")}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExport} disabled={isExporting}>
                  {isExporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  {isExporting ? "Exporting..." : "Export PDF"}
                </DropdownMenuItem>
                {!readOnly ? (
                  <DropdownMenuItem onClick={() => setShareOpen(true)}>
                    <Share2 className="h-4 w-4 mr-2" />
                    Share Report
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {!isPublic && activeInsight?.validation && (
        <ValidationPanel validation={activeInsight.validation} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2 text-sm">
            <Eye className="h-4 w-4 text-primary" />
            Executive Summary
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Based on our analysis of{" "}
            <span className="font-medium text-foreground">{resolvedKeywordsAnalyzed} keywords</span>{" "}
            across your product landscape, we've identified{" "}
            <span className="font-medium text-foreground">{filteredTopKps.length} high-priority keywords</span>{" "}
            with the strongest KPS scores,{" "}
            <span className="font-medium text-foreground">{filteredHiddenGems.length} hidden gem opportunities</span>{" "}
            with favorable competition dynamics, and{" "}
            <span className="font-medium text-foreground">{filteredTrending.length} trending keywords</span>{" "}
          </p>
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <Sparkles className="h-4 w-4 text-transparent" style={{ fill: "url(#signal-grad)" }} />
                <svg width="0" height="0" style={{ position: "absolute" }}>
                  <defs>
                    <linearGradient id="signal-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#342799" />
                      <stop offset="50%" stopColor="#A242A2" />
                      <stop offset="100%" stopColor="#FF534C" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="bg-gradient-to-r from-[#342799] via-[#A242A2] to-[#FF534C] bg-clip-text text-transparent">
                  Signal AI Recommendations
                </span>
              </h3>
              {!isPublic && !readOnly && (
                <button type="button" onClick={toggleAllAiRecs} className="text-sm text-primary hover:text-primary/80 transition-colors">
                  {allAiRecsSelected ? "Deselect all" : "Select all"}
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Our proprietary Signal AI reviews keywords across {filteredAiRecs.length} dimensions to bring you the most impactful keywords for your business right now.
            </p>
          </div>

          {!isPublic && !readOnly && (
            <div data-html2canvas-ignore="true">
              <BulkActionsBar
                selectedCount={selectedAiRecs.size}
                onClearSelection={() => setSelectedAiRecs(new Set())}
              >
                <DropdownMenuItem
                  className={cn("text-xs gap-2", isMasterListBusy && "pointer-events-none opacity-60")}
                  onSelect={() => handleAiRecsAddToMasterList()}
                >
                  <Star className="h-3.5 w-3.5" />Add to Master List
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-xs gap-2"
                  onSelect={() => handleAiRecsAddToRankTracker()}
                >
                  <TrendingUp className="h-3.5 w-3.5" />
                  Add to Rank Tracker
                </DropdownMenuItem>
              </BulkActionsBar>
            </div>
          )}

          <div className="space-y-1.5">
            {filteredAiRecs.length === 0 ? (
              <p className="text-xs text-muted-foreground p-3 bg-muted/20 rounded-lg">No recommendations match your search.</p>
            ) : filteredAiRecs.map((rec) => {
              const isSelected = !isPublic && !readOnly && selectedAiRecs.has(rec.keyword);
              return (
                <div
                  key={rec.keyword}
                  className={cn(
                    "flex items-center gap-2.5 p-2 rounded-lg border bg-gradient-to-r from-[#8B80D4]/10 via-[#D4A0D4]/10 to-[#FFADA9]/10 border-[#D4A0D4]/20",
                    !isPublic && !readOnly && "cursor-pointer transition-colors",
                    isSelected && "ring-1 ring-[#A242A2]/40",
                  )}
                  onClick={() => !isPublic && !readOnly && toggleAiRec(rec.keyword)}
                >
                  {!isPublic && !readOnly ? (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleAiRec(rec.keyword)}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-shrink-0 bg-white"
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground">
                      <TruncatedText>{rec.keyword}</TruncatedText>
                    </p>
                    <p className="text-[10px] text-muted-foreground">{rec.note}</p>
                  </div>
                  <Badge className="border-0 text-white bg-gradient-to-r from-[#342799] via-[#A242A2] to-[#FF534C] gap-1 flex-shrink-0 whitespace-nowrap">
                    <Sparkle className="h-3 w-3 text-white" />
                    Signal AI
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Separator />

      {!isPublic && !readOnly && (
        <div data-html2canvas-ignore="true">
          <BulkActionsBar
            selectedCount={selectedKeywords.size}
            onClearSelection={() => setSelectedKeywords(new Set())}
          >
            <DropdownMenuItem
              className={cn("text-xs gap-2", isMasterListBusy && "pointer-events-none opacity-60")}
              onSelect={() => handleAddToMasterList()}
            >
              <Star className="h-3.5 w-3.5" />
              {isBulkMasterListUpdating ? "Adding..." : "Add to Master List"}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-xs gap-2"
              onSelect={() => handleAddToRankTracker()}
            >
              <TrendingUp className="h-3.5 w-3.5" />
              Add to Rank Tracker
            </DropdownMenuItem>
          </BulkActionsBar>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-semibold text-sm text-foreground">Additional Recommendations</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="space-y-3">
            <SectionHeader
              keywords={filteredTopKps}
              icon={<Trophy className="h-4 w-4 text-amber-500" />}
              title="Top Priority"
              badgeLabel="Highest KPS"
              tip="Focus SEO efforts on these keywords—best balance of volume, competition, and relevance."
            />
             <div className="space-y-2">
              {filteredTopKps.length === 0 ? (
                <p className="text-xs text-muted-foreground p-3 bg-muted/20 rounded-lg">No keywords match your filters.</p>
              ) : filteredTopKps.map((kw) => (
                <KeywordCard key={kw.id} kw={kw} variant="priority" />
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <SectionHeader
              keywords={filteredHiddenGems}
              icon={<Gem className="h-4 w-4 text-purple-500" />}
              title="Hidden Gems"
              badgeLabel="Low Competition"
              tip="Establish ranking on these before competition increases. Target in your next listing optimization."
              tipColorClass="text-muted-foreground"
            />
            {filteredHiddenGems.length > 0 ? (
              <div className="space-y-2">
                {filteredHiddenGems.map((kw) => (
                  <KeywordCard key={kw.id} kw={kw} variant="gem" />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground p-3 bg-muted/20 rounded-lg">No keywords match your filters.</p>
            )}
          </div>

          <div className="space-y-3">
            <SectionHeader
              keywords={filteredTrending}
              icon={<Zap className="h-4 w-4 text-[hsl(var(--trend-positive))]" />}
              title="Trending"
              badgeLabel="Rising Volume"
              tip="Incorporate these into PPC campaigns and backend keywords to capitalize on growing search demand."
              tipColorClass="text-muted-foreground"
            />
            <div className="space-y-2">
              {filteredTrending.length === 0 ? (
                <p className="text-xs text-muted-foreground p-3 bg-muted/20 rounded-lg">No keywords match your filters.</p>
              ) : filteredTrending.map((kw) => (
                <KeywordCard key={kw.id} kw={kw} variant="trending" />
              ))}
            </div>
          </div>
        </div>
      </div>

      {!isPublic && !readOnly && (
        <ShareLinkModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          shareableType="seo_insights"
          frozenPayload={{
            topPriority: filteredTopKps.map((k) => ({ id: k.id, search: k.search, search_volume: k.search_volume, kps_raw: k.kps_raw, hero_relevance: k.hero_relevance })),
            hiddenGems: filteredHiddenGems.map((k) => ({ id: k.id, search: k.search, search_volume: k.search_volume, kps_raw: k.kps_raw, hero_relevance: k.hero_relevance })),
            trending: filteredTrending.map((k) => ({ id: k.id, search: k.search, search_volume: k.search_volume, kps_raw: k.kps_raw, hero_relevance: k.hero_relevance })),
            aiRecs: filteredAiRecs,
            summary: {
              keywordsAnalyzed: activeInsight?.keywords_analyzed ?? 0,
              topPriorityCount: insightTopPriority.length,
              hiddenGemsCount: insightHiddenGems.length,
              trendingCount: insightTrending.length,
            },
          }}
          resourceName="SEO Insights Report"
        />
      )}


    </div>
  );
}
